import { FC, MouseEventHandler, ReactNode, useMemo } from 'react'
import { produce } from 'immer'
import { GridLayout, GridLayoutItem } from '@progress/kendo-react-layout'

import { LinkButton } from '../elements'
import AppMenuCategory from './AppMenuCategory'
import { useAppSelector } from '../../stores'
import { Authority } from '../../gateway/system/Authority'

const ITEM_WIDTH = 180
const ITEM_HEIGHT = 50

type MenuInfoConfigList = Array<{
  menuCategoryName:
  | '物件情報'
  | '業者ヒアリング'
  | '山積シミュレーション'
  | 'マスタ管理'
  linkInfoList: Array<{
    /** 権限制御変数 */
    access: boolean
    linkChildren: ReactNode
    directPath: string
  }>
}>
const MENU_INFO_CONFIG_LIST: MenuInfoConfigList = [
  // #region 物件情報 start
  {
    menuCategoryName: '物件情報',
    linkInfoList: [
      {
        access: true,
        linkChildren: '物件情報取込み',
        directPath: '/building/import'
      },
      {
        access: true,
        linkChildren: '物件情報一覧',
        directPath: '/building/search'
      }
    ]
  },
  // #endregion 物件情報 end
  // #region 業者ヒアリング start
  {
    menuCategoryName: '業者ヒアリング',
    linkInfoList: [
      {
        access: true,
        linkChildren: '業者ヒアリング状況確認',
        directPath: '/supplier/status-check'
      }
    ]
  },
  // #endregion 業者ヒアリング end
  // #region 山積シミュレーション start
  {
    menuCategoryName: '山積シミュレーション',
    linkInfoList: [
      {
        access: true,
        linkChildren: '山積シミュレーション',
        directPath: '/simulation/execution'
      },
      {
        access: true,
        linkChildren: '山積対象物件一覧',
        directPath: '/simulation/building-search'
      },
      {
        access: true,
        linkChildren: '山積シミュレーション出力',
        directPath: '/simulation/export'
      },
      {
        access: true,
        linkChildren: (
          <>
            消化高シミュレーション
            <br />
            出力
          </>
        ),
        directPath: '/simulation/digestive-simulation'
      },
      {
        access: true,
        linkChildren: '山積管理表出力',
        directPath: '/simulation/labor-stack-management-table'
      },
      {
        access: true,
        linkChildren: '山積状況一覧',
        directPath: '/simulation/status-list'
      }
    ]
  },
  // #endregion 山積シミュレーション end
  // #region マスタ管理 start
  {
    menuCategoryName: 'マスタ管理',
    linkInfoList: [
      {
        access: true,
        linkChildren: '業者',
        directPath: '/maintenance/supplier'
      }
      // {
      //   access: true,
      //   linkChildren: '利用者権限',
      //   directPath: '/maintenance/auth'
      // },
      // {
      //   access: true,
      //   linkChildren: '構造比率・歩掛',
      //   directPath: '/maintenance/structure'
      // },
      // {
      //   access: true,
      //   linkChildren: '工期設定',
      //   directPath: '/maintenance/const-period'
      // },
      // {
      //   access: true,
      //   linkChildren: 'コード名称',
      //   directPath: '/maintenance/code-name'
      // }
    ]
  }
  // #endregion マスタ管理 end
]

interface AppMenuProps {
  onMenuClicked?: MouseEventHandler
}

export const AppMenu: FC<AppMenuProps> = ({ onMenuClicked }) => {
  const userProp = useAppSelector((state) => state.common.userProp)

  const userAuthority = userProp?.authority
  /** 物件情報開示フラグ */
  const buildingDisclose = userProp?.supplier?.buildingDisclose ?? false

  const memoMenuInfoList = useMemo(() => {
    // @see https://immerjs.github.io/immer/produce
    const menuInfoList = produce(MENU_INFO_CONFIG_LIST, (draft) => {
      draft.forEach((menuInfo) => {
        // #region「物件情報」権限制御
        if (menuInfo.menuCategoryName === '物件情報') {
          menuInfo.linkInfoList.forEach((linkInfo) => {
            if (linkInfo.directPath === '/building/import') {
              const access = userAuthority === Authority.Builder

              linkInfo.access = access
            }

            if (linkInfo.directPath === '/building/search') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch ||
                // 業者 物件情報開示フラグ === true(開示)のみ利用可能
                (userAuthority === Authority.Supplier && buildingDisclose)

              linkInfo.access = access
            }
          })
        }
        // #endregion「物件情報」権限制御

        // #region「業者ヒアリング」権限制御
        if (menuInfo.menuCategoryName === '業者ヒアリング') {
          menuInfo.linkInfoList.forEach((linkInfo) => {
            if (linkInfo.directPath === '/supplier/status-check') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch ||
                userAuthority === Authority.Supplier

              linkInfo.access = access
            }
          })
        }
        // #endregion「業者ヒアリング」権限制御

        // #region「山積シミュレーション」権限制御
        if (menuInfo.menuCategoryName === '山積シミュレーション') {
          menuInfo.linkInfoList.forEach((linkInfo) => {
            if (linkInfo.directPath === '/simulation/execution') {
              const access =
                userAuthority === Authority.Builder

              linkInfo.access = access
            }

            if (linkInfo.directPath === '/simulation/building-search') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }

            if (linkInfo.directPath === '/simulation/export') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }

            if (linkInfo.directPath === '/simulation/digestive-simulation') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }

            if (
              linkInfo.directPath === '/simulation/labor-stack-management-table'
            ) {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }

            if (linkInfo.directPath === '/simulation/status-list') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material

              linkInfo.access = access
            }
          })
        }
        // #endregion「山積シミュレーション」権限制御

        // #region「マスタ管理」権限制御
        if (menuInfo.menuCategoryName === 'マスタ管理') {
          menuInfo.linkInfoList.forEach((linkInfo) => {
            // 業者マスタメンテナンス（検索）
            if (linkInfo.directPath === '/maintenance/supplier') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }

            // 権限マスタメンテナンス（検索）
            if (linkInfo.directPath === '/maintenance/auth') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }

            if (linkInfo.directPath === '/maintenance/structure') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch
              linkInfo.access = access
            }

            if (linkInfo.directPath === '/maintenance/const-period') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }

            if (linkInfo.directPath === '/maintenance/code-name') {
              const access =
                userAuthority === Authority.Builder ||
                userAuthority === Authority.Material ||
                userAuthority === Authority.Branch

              linkInfo.access = access
            }
          })
        }
        // #endregion「マスタ管理」権限制御
      })
    })

    return menuInfoList
  }, [buildingDisclose, userAuthority])

  const memoAccessCategoryNameList = useMemo(() => {
    const accessMenuInfoList = memoMenuInfoList.filter((menuInfo) => {
      const accessLinkButton = menuInfo.linkInfoList.find(
        (linkInfo) => linkInfo.access
      )
      return accessLinkButton !== undefined
    })

    return accessMenuInfoList.map((accessMenuInfo) => {
      return accessMenuInfo.menuCategoryName
    })
  }, [memoMenuInfoList])

  return (
    <div className="pcy-app-menu">
      {memoMenuInfoList.flatMap(m => m.linkInfoList).every(m => !m.access)
        ? (<div style={{ margin: '50px', textAlign: 'center', color: 'gray' }}>利用できる機能がありません。権限があるユーザでログインしてください。</div>)
        : <></>
      }
      <GridLayout
        gap={{ cols: 30, rows: 60 }}
        cols={[{ width: '50%' }, { width: '50%' }]}
      >
        {memoMenuInfoList.map((menuInfo) => {
          const accessMenuCategoryIndex = memoAccessCategoryNameList.findIndex(
            (accessCategoryName) => {
              return accessCategoryName === menuInfo.menuCategoryName
            }
          )

          // メニュー分類中で、利用可のボタンがありません
          // renderしない
          if (accessMenuCategoryIndex === -1) {
            return null
          }

          return (
            <GridLayoutItem
              key={`${menuInfo.menuCategoryName}_${accessMenuCategoryIndex}`}
              // 2列だけ
              col={accessMenuCategoryIndex % 2 === 0 ? 1 : 2}
              row={
                accessMenuCategoryIndex % 2 === 0
                  ? accessMenuCategoryIndex / 2 + 1
                  : (accessMenuCategoryIndex + 1) / 2
              }
            >
              <AppMenuCategory
                caption={menuInfo.menuCategoryName}
                itemWidth={ITEM_WIDTH}
                itemHeight={ITEM_HEIGHT}
                gap={5}
              >
                {menuInfo.linkInfoList
                  // 権限がありません、renderしない
                  .filter((linkInfo) => linkInfo.access)
                  .map((linkInfo, index) => {
                    return (
                      <LinkButton
                        key={`${linkInfo.directPath}_${index}`}
                        to={linkInfo.directPath}
                        onClick={onMenuClicked}
                      >
                        {linkInfo.linkChildren}
                      </LinkButton>
                    )
                  })}
              </AppMenuCategory>
            </GridLayoutItem>
          )
        })}
      </GridLayout>
    </div>
  )
}
